"use client";

import { oneTap, useSession } from "@/lib/auth/client";

import { routes } from "@/lib/routing/routes";
/**
 * GoogleOneTap — Google One Tap (FedCM) silent-auth prompt.
 *
 * Source: features/auth-core.yml components.shared (GoogleOneTap);
 *         features/auth-core.yml acceptance:141 (Google One Tap (FedCM)
 *         wired when NEXT_PUBLIC_GOOGLE_CLIENT_ID set; suppresses if
 *         already authenticated; respects browser FedCM gate).
 *
 * Renders nothing visible — triggers the browser-native One Tap prompt
 * via Google Identity Services (or FedCM in supported browsers).
 *
 * Does NOT show if:
 *   - User is already authenticated (session.user present)
 *   - NEXT_PUBLIC_GOOGLE_CLIENT_ID is not set (env-flag off)
 *   - Running server-side
 *
 * Authoring contract: forwardRef is N/A (returns null — no DOM target);
 * named export + JSDoc + props interface are the §8.2 conformance bar
 * for null-rendering domain components.
 */

import { useEffect, useRef } from "react";

export function GoogleOneTap({ callbackURL = routes.crm.dashboard, autoSelect = true }: {
    callbackURL?: string;
    autoSelect?: boolean;
}) {
    const { data: session, isPending } = useSession();
    const triggeredRef = useRef(false);

    useEffect(() => {
        if (isPending || session?.user || triggeredRef.current) {
            return;
        }
        if (!process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID) {
            return;
        }

        triggeredRef.current = true;

        oneTap({ callbackURL, autoSelect, cancelOnTapOutside: true }).catch(() => {
            // Silently handle dismissal/errors — One Tap is supplementary
        });
    }, [isPending, session, callbackURL, autoSelect]);

    return null;
}
